import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { X, Upload, Send, Mic, MicOff, CheckCircle2, FileText, Loader, Image as ImageIcon, RotateCcw } from 'lucide-react';
import { neonQuery } from '../services/api';
import { getAccessToken } from '../services/auth';
import {
  prepareAttachment, blocksFor, forStorage, filesFromDrop, classifyFile,
  dragCarriesFiles, explainSendFailure, MAX_ATTACH_BYTES, prettySize,
} from '../lib/chatAttachments';

// "Walk me through this" — opens on a single task and stays with the person until it
// is finished. Written for someone learning the role: the steps are always visible,
// the file lands where it needs to, and Claude answers questions about THIS task
// without anyone leaving CPx.
//
// The Claude call goes through claude-proxy (server-side key, authenticated, model
// allowlisted). Dictation uses the browser's own speech recognition, so there is no
// dependency and nothing is uploaded anywhere to transcribe it.

const MODEL = 'claude-sonnet-4-6'; // the only model claude-proxy allows

const MAX_ATTACHMENTS = 4;
// Only the recent turns carry their bytes upstream. Older attachments stay in the
// transcript as a filename, so a long conversation cannot grow past the body cap.
const IMAGE_HISTORY_TURNS = 6;

// A hard stop on the tool loop. Four rounds covers "look it up, then answer"; more
// than that is a model going in circles, and every round is a live API call.
const MAX_TOOL_ROUNDS = 4;

// The three things this can do besides talk. They are the same tools, backed by the
// same neon handlers, that the board-level chat has had since it shipped -- see
// BrainChat.js. TaskCoach went without them, and on 25 Aug Rachael asked it twice to
// "save this to my brain" and was told, correctly at the time, that it could not.
// She had just settled the channel order for the contact board in this window, and
// the only way that decision survived was that she typed it out again in an email.
const TOOLS = [
  {
    name: 'search_the_board',
    description:
      'Search this board for anything already written down: tasks, phases, findings, ' +
      'open questions, the instructions on a task, and notes people have left. Use it ' +
      'before answering any question about what was decided, what is outstanding, or ' +
      'how something is done here. Prefer one or two distinctive words over a phrase.',
    input_schema: {
      type: 'object',
      properties: { q: { type: 'string', description: 'A word or short phrase to look for.' } },
      required: ['q'],
    },
  },
  {
    name: 'write_it_down',
    description:
      'Record something worth keeping on this board so it outlives this conversation. ' +
      'Use it the moment the person settles a question, corrects a wrong assumption, ' +
      'explains how something really works, or gives you the answer the task was ' +
      'asking for. Do it without being asked and say in one line that you did: they ' +
      'should never have to wonder whether their answer was kept, and they should ' +
      'never be told to go and pass it on by hand.',
    input_schema: {
      type: 'object',
      properties: {
        what: { type: 'string', description: 'The heading. One clear sentence saying the thing itself.' },
        why: { type: 'string', description: 'The detail: how it was established, what it means, what to do with it.' },
        kind: { type: 'string', enum: ['finding', 'note'], description: 'finding for durable knowledge, note for a passing remark.' },
        phase: { type: 'string', description: 'Optional phase to file it under.' },
      },
      required: ['what'],
    },
  },
  {
    name: 'send_to_greg',
    description:
      "Put something on Greg's list. Use it when the task turns up work that is his to " +
      'decide or do: a question only he can answer, a decision that is blocking this ' +
      'task, a number that looks wrong. It writes a real item he sees in his morning ' +
      'brief. Say so afterwards, and never use it for something the person in front of ' +
      'you can do themselves.',
    input_schema: {
      type: 'object',
      properties: {
        title: { type: 'string', description: 'One line, written as the action he needs to take.' },
        detail: { type: 'string', description: 'The background he needs to act without coming back to ask.' },
      },
      required: ['title'],
    },
  },
];

// `config` is the board's own config object, the same one BrainBoard hands to
// BrainChat: prefix, sectionLabel, chatGuidance. `prefix` stays as a fallback so any
// caller written before this took a config keeps working unchanged.
//
// This used to be accounting-only, and not by accident of naming: the system prompt
// said "one specific accounting task", the quick replies offered "Where do I find
// this in QuickBooks?", and files were stored under an accounting prefix. When the
// board became shared across three domains, BrainChat was parameterised and this was
// not. So the first line Rachael saw on a contact centre task was Claude explaining
// that this was not an accounting task -- which is what she reported.
export default function TaskCoach({ item, files, notes, onClose, onChanged, config, prefix: prefixProp }) {
  const cfg = config || {};
  const prefix = cfg.prefix || prefixProp || 'acct';
  const domain = (cfg.sectionLabel || 'accounting').toLowerCase();
  // R2 keeps files under a folder per domain, so a contact centre screenshot does not
  // land in the accounting drawer.
  const storePrefix = (cfg.sectionLabel || 'accounting').toLowerCase().replace(/[^a-z0-9]+/g, '-');
  // The openers offered on an empty conversation. The third one used to be "Where do
  // I find this in QuickBooks?" on every board, which is the wrong question to hand
  // somebody working a contact centre task.
  const OPENERS = [
    "I'm ready, walk me through it",
    "I'm stuck on step 3",
    prefix === 'acct' ? 'Where do I find this in QuickBooks?' : 'What is this task actually asking me?',
  ];
  // Stable across renders so the memoised effects below can depend on it.
  const q = useCallback((op) => `${prefix}_${op}`, [prefix]);
  const [messages, setMessages] = useState([]);
  const [restored, setRestored] = useState(false);
  const [loadingChat, setLoadingChat] = useState(true);
  const [draft, setDraft] = useState('');
  const [attachments, setAttachments] = useState([]); // screenshots riding on the next message
  const [thinking, setThinking] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');
  const [listening, setListening] = useState(false);
  const [dragging, setDragging] = useState(false);
  const recogRef = useRef(null);
  const stoppedByUser = useRef(true);
  const draftRef = useRef('');
  const endRef = useRef(null);
  const fileRef = useRef(null);
  const dragDepth = useRef(0);
  const attachRef = useRef([]);

  const myFiles = (files || []).filter(f => f.item_id === item.id);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, thinking]);
  useEffect(() => { draftRef.current = draft; }, [draft]);
  useEffect(() => { attachRef.current = attachments; }, [attachments]);

  // ── the conversation survives closing the panel ───────────────────────────
  // Mercedes, 20 Aug: she closes this to go and look something up, comes back, and it
  // has started over, so she is re-explaining her own question every time. Stored per
  // person per task, so it is there again on reopen, after a reload, and on another
  // machine. Per person because a conversation is a working thread, and two people on
  // the same task should not be writing over each other.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const d = await neonQuery(q('chat_get'), { itemId: item.id });
        if (!alive) return;
        const saved = Array.isArray(d?.messages) ? d.messages : [];
        setMessages(saved);
        setRestored(saved.length > 0);
      } catch (_) {
        // No transcript is not an error. Start fresh rather than blocking the panel.
      } finally { if (alive) setLoadingChat(false); }
    })();
    return () => { alive = false; };
  }, [item.id, q]);

  // Image bytes are deliberately dropped on the way to the database. The picture
  // itself is saved to the task as a file; the transcript keeps only its name.
  const persist = useCallback(async (list) => {
    try {
      await neonQuery(q('chat_save'), {
        itemId: item.id,
        messages: list.map(m => ({
          role: m.role,
          text: m.text,
          images: (m.images || []).map(i => ({ name: i.name, key: i.key || null })),
          // Keys, never contents. A PDF or an export stored in R2 stays readable
          // when the conversation is reopened tomorrow; the bytes and the extracted
          // text would be megabytes in a column we read on every open.
          docs: (m.docs || []).map(forStorage),
        })),
      });
    } catch (_) {
      // The conversation still works on screen if the save fails.
    }
  }, [item.id, q]);

  const clearChat = async () => {
    if (!window.confirm('Clear this conversation and start again? The files you attached stay on the task.')) return;
    setMessages([]); setRestored(false); setErr('');
    try { await neonQuery(q('chat_clear'), { itemId: item.id }); } catch (_) {}
  };

  // ── dictation (browser-native, no dependency) ─────────────────────────────
  const SR = typeof window !== 'undefined'
    && (window.SpeechRecognition || window.webkitSpeechRecognition);

  // Mercedes, 19 Aug: "if you stop recording and start again, it erases everything it
  // caught before". Two causes, both fixed here. The transcript was written over the
  // whole draft instead of being appended to what was already there, and Chrome ends
  // a recognition session on its own after a pause, which looked like a stop.
  const toggleMic = () => {
    if (!SR) { setErr('This browser will not do speech. Chrome or Edge will.'); return; }
    if (listening) { stoppedByUser.current = true; recogRef.current?.stop(); return; }
    startListening();
  };

  const startListening = () => {
    const r = new SR();
    r.continuous = true;
    r.interimResults = true;
    r.lang = 'en-US';
    // Whatever is already in the box stays. Dictation adds to it.
    const base = draftRef.current ? draftRef.current.replace(/\s+$/, '') + ' ' : '';
    let settled = '';
    r.onresult = (e) => {
      let interim = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) settled += t; else interim += t;
      }
      setDraft((base + settled + interim).replace(/\s+/g, ' ').trimStart());
    };
    r.onerror = (e) => {
      if (e.error === 'no-speech' || e.error === 'aborted') return; // harmless, onend handles it
      setErr(`Microphone: ${e.error}`);
      stoppedByUser.current = true;
    };
    r.onend = () => {
      // Chrome ends the session on a pause. Unless she pressed stop, pick straight
      // back up so a breath does not end the recording.
      if (!stoppedByUser.current) { try { r.start(); return; } catch (_) { /* fall through */ } }
      setListening(false);
    };
    recogRef.current = r;
    setErr('');
    stoppedByUser.current = false;
    r.start();
    setListening(true);
  };

  useEffect(() => () => { stoppedByUser.current = true; try { recogRef.current?.stop(); } catch (_) {} }, []);

  // ── files coming in ───────────────────────────────────────────────────────
  // Mercedes, 21 Aug: 'when I click "Walk me through this" on a task and attach a
  // PDF, the chat cannot read it. It only works with screenshots.' It did not: a
  // non-image was uploaded to the task and never shown to Claude, so she got an
  // answer about a document nobody had read.
  //
  // Now every file does both jobs. It attaches to the task, because a document that
  // answers a question is evidence and belongs where the next person will look for
  // it, AND it goes into the message so the answer is about what is actually in it.
  // PDFs are read page by page, spreadsheets and bank downloads are turned into
  // text on the way in, and the handful of formats nobody can read are still kept
  // and say so in a sentence she can act on.
  const takeFiles = async (list) => {
    const incoming = Array.from(list || []).filter(Boolean);
    if (!incoming.length) {
      setErr('The browser did not hand over anything it can read. That happens with a picture dragged out of an email body, which is not a file. or use "choose a file".');
      return;
    }
    setErr('');
    const room = MAX_ATTACHMENTS - attachRef.current.length;
    if (room <= 0) {
      setErr(`That is as many files as one message will take (${MAX_ATTACHMENTS}). Send this one first.`);
      return;
    }
    setUploading(true);
    try {
      for (const file of incoming.slice(0, room)) {
        try {
          // Screenshots are the exception to `store`. They are uploaded at SEND
          // time instead, so a picture pasted and then thought better of does not
          // leave a file on the task.
          const isPic = classifyFile(file) === 'image';
          const att = await prepareAttachment(file, { prefix: storePrefix, store: !isPic });
          att.id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
          if (att.r2Key) await registerOnTask(att, file);
          attachRef.current = [...attachRef.current, att];
          setAttachments(attachRef.current);
          if (att.note) setErr(att.note);
        } catch (e) { setErr(e.message || 'That file would not open.'); }
      }
      if (incoming.length > room) setErr(`Only the first ${room} went on. Send this message, then add the rest.`);
    } finally { setUploading(false); }
  };

  // prepareAttachment has already put the bytes in R2; this is only the row that
  // makes it show up in the task's file list. Silent, so attaching four pages of a
  // statement does not bury the real notes under four "file uploaded" lines.
  const registerOnTask = async (att, file) => {
    try {
      await neonQuery(q('file_register'), {
        itemId: item.id, r2Key: att.r2Key, filename: att.name,
        contentType: file.type, byteSize: file.size, silent: true,
      });
      onChanged && onChanged();
    } catch (_) { /* it is still readable in the chat, which is what she asked for */ }
  };

  const removeAttachment = (id) => setAttachments(a => a.filter(x => x.id !== id));

  // ── drag and drop ─────────────────────────────────────────────────────────
  // Mercedes, 20 Aug: it says you can drag a file in and nothing happens. The panel is
  // now a drop target end to end, it lights up so you can see it has taken the drag,
  // and the window listeners below are the important half: without them a drop that
  // lands a few pixels outside makes Chrome open the file and throw the page away
  // mid-task, which reads exactly like "drag and drop does not work".
  useEffect(() => {
    const swallow = (e) => { e.preventDefault(); };
    window.addEventListener('dragover', swallow);
    window.addEventListener('drop', swallow);
    return () => {
      window.removeEventListener('dragover', swallow);
      window.removeEventListener('drop', swallow);
    };
  }, []);

  const onDragEnter = (e) => {
    if (!dragCarriesFiles(e.dataTransfer)) return;
    e.preventDefault();
    dragDepth.current += 1;
    setDragging(true);
  };
  const onDragOver = (e) => {
    if (!dragCarriesFiles(e.dataTransfer)) return;
    e.preventDefault();
    // Chrome only allows the drop if the effect is set on every dragover.
    try { e.dataTransfer.dropEffect = 'copy'; } catch (_) {}
    if (!dragging) setDragging(true);
  };
  const onDragLeave = () => {
    // Counted, not toggled: dragging across a child fires leave on the one behind.
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragging(false);
  };
  // Async on purpose: an Outlook attachment has to be pulled out of the message
  // before it is a file at all. filesFromDrop does the synchronous half of that
  // before it awaits anything, which is the whole trick.
  const onDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current = 0;
    setDragging(false);
    takeFiles(await filesFromDrop(e.dataTransfer));
  };

  const uploadFile = async (file, { silent = false } = {}) => {
    if (!file) return null;
    setUploading(true); setErr('');
    try {
      const pres = await fetch('/.netlify/functions/r2-upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken()}` },
        body: JSON.stringify({
          action: 'presign-doc', filename: file.name || `pasted-${Date.now()}.png`,
          contentType: file.type || 'application/octet-stream', prefix: storePrefix,
        }),
      });
      const p = await pres.json();
      if (!pres.ok || !p.uploadUrl) throw new Error(p.error || 'Could not prepare the upload.');

      const put = await fetch(p.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': p.contentType || file.type || 'application/octet-stream' },
        body: file,
      });
      if (!put.ok) throw new Error(`Upload failed (${put.status}). If this keeps happening, tell Greg.`);

      await neonQuery(q('file_register'), {
        itemId: item.id, r2Key: p.key, filename: file.name,
        contentType: file.type, byteSize: file.size, silent,
      });
      if (!silent) {
        setMessages(m => {
          const next = [...m, { role: 'system', text: `${file.name} uploaded. It is saved against this task.` }];
          persist(next);
          return next;
        });
      }
      onChanged && onChanged();
      return p.key;
    } catch (e) { setErr(e.message); return null; } finally { setUploading(false); }
  };

  const viewFile = async (f) => {
    try {
      const r = await fetch('/.netlify/functions/r2-upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken()}` },
        body: JSON.stringify({ action: 'presign-read', key: f.r2_key }),
      });
      const d = await r.json();
      if (d.url) window.open(d.url, '_blank');
      else setErr(d.error || 'Could not open that file.');
    } catch (e) { setErr(e.message); }
  };

  // ── chat ──────────────────────────────────────────────────────────────────
  const systemPrompt = [
    `You are helping a member of staff at The Crab Place, a family seafood business in Crisfield, Maryland, complete one specific ${domain} task inside their admin tool.`,
    `They are capable and careful but may still be learning this side of the business, so explain things plainly and never assume they know ${domain} jargon. If a term is unavoidable, say what it means in the same breath.`,
    'Keep answers short. Give one step at a time when they are working through something, and wait for them to come back.',
    'They can paste screenshots and attach files straight into this conversation: PDFs, spreadsheets, CSV exports and bank downloads all come through readable. When they do, read what is actually in the file and answer about that, rather than describing it back to them.',
    'If a message says a file was attached but not readable, tell them plainly what to do about it (save it as a PDF, or export it to CSV) instead of guessing at what was in it.',
    'If they seem stuck or something does not match the instructions, say so plainly and suggest what to check. It is fine to say you are not sure and to say who would know.',
    '',
    'THIS BOARD IS WRITEABLE AND YOU CAN SEE IT.',
    'search_the_board reads what the team has already written down. Use it before answering anything about what was decided or how something is done here.',
    'write_it_down puts something on the board permanently. Use it the moment they settle the question the task is asking, and say in one line that you did.',
    'Never tell them you cannot remember between conversations, and never tell them to copy an answer out and send it to somebody by hand. If an answer matters, write it down. If it is a decision only Greg can make, use send_to_greg. Handing work back to a person to retype is the failure this board exists to remove.',
    '',
    prefix === 'acct'
      ? 'Never tell them to change, delete or post anything in QuickBooks. This work is read-and-export only. If a task seems to need a change, say it needs Greg.'
      : '',
    cfg.chatGuidance || '',
    'Do not use em dashes or en dashes. Use commas, periods or brackets.',
    'End on the outcome and stop. Do not finish with a list of things to flag, things to',
    'watch, or things worth knowing. They are working through a task, and every extra item',
    'is one more thing to hold. Say it out loud only if it beats the step they are on.',
    'The exceptions that always get said: money about to move wrongly, customer data',
    'exposed, or a number they are about to act on being wrong.',
    '',
    'THE TASK THEY ARE ON:',
    `Title: ${item.title}`,
    item.detail ? `Background: ${item.detail}` : '',
    item.how_to ? `\nThe written instructions they can see on screen:\n${item.how_to}` : '',
    myFiles.length ? `\nThey have already attached: ${myFiles.map(f => f.filename).join(', ')}` : '\nThey have not attached a file yet.',
    (notes || []).length ? `\nNotes recorded on this task:\n${(notes || []).map(n => `- ${n.author}: ${n.body}`).join('\n')}` : '',
  ].filter(Boolean).join('\n');

  // A message is plain text, or text plus whatever was attached to it. Only the
  // recent turns send their contents; anything older is named instead, so the thread
  // still reads correctly and the request body cannot grow without limit.
  //
  // A document that lives in R2 gets a FRESH signed URL here on every call. The
  // signature only lasts an hour and a conversation about the same statement can
  // easily run longer than that.
  const toApi = async (list) => Promise.all(list.map(async (m, i) => {
    const recent = i >= list.length - IMAGE_HISTORY_TURNS;
    const atts = [...(m.images || []), ...(m.docs || [])];
    const lead = [];
    const named = [];
    for (const a of atts) {
      let blocks = [];
      // An image descriptor from the old shape has no `kind`, so name it here.
      if (recent) { try { blocks = await blocksFor(a.kind ? a : { ...a, kind: 'image' }); } catch (_) { blocks = []; } }
      if (blocks.length) lead.push(...blocks); else named.push(a.name);
    }
    const text = [
      m.text,
      named.length ? `(shared earlier: ${named.join(', ')})` : '',
    ].filter(Boolean).join('\n').trim() || 'Have a look at this.';
    if (!lead.length) return { role: m.role, content: text };
    return { role: m.role, content: [...lead, { type: 'text', text }] };
  }));

  const callClaude = async (apiMsgs) => {
    const res = await fetch('/.netlify/functions/claude-proxy', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken()}` },
      body: JSON.stringify({
        model: MODEL,
        max_tokens: 900,
        system: systemPrompt,
        tools: TOOLS,
        messages: apiMsgs,
      }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || `Claude is not answering (${res.status}).`);
    return data;
  };

  // The same neon handlers the board-level chat uses, so a thing written down from a
  // task and a thing written down from the board land in exactly the same place.
  const runTool = async (name, input) => {
    if (name === 'search_the_board') {
      const d = await neonQuery(q('search'), { q: String(input?.q || '') });
      const items = d?.items || [];
      const notesFound = d?.notes || [];
      if (!items.length && !notesFound.length) return `Nothing on the board matches "${input?.q}".`;
      const lines = [];
      if (items.length) {
        lines.push('ITEMS:');
        for (const i of items) {
          lines.push(`#${i.id} [${i.kind}/${i.status}${i.owner ? ', ' + i.owner : ''}] ${i.title}`);
          if (i.detail) lines.push(`  ${i.detail}`);
          if (i.how_to) lines.push(`  instructions: ${String(i.how_to).slice(0, 600)}`);
        }
      }
      if (notesFound.length) {
        lines.push('NOTES:');
        for (const n of notesFound) lines.push(`- ${n.author || 'someone'} on ${n.item_title || 'the board'}: ${n.body}`);
      }
      return lines.join('\n');
    }
    if (name === 'write_it_down') {
      const d = await neonQuery(q('remember'), {
        what: input?.what, why: input?.why, kind: input?.kind, phase: input?.phase || item.phase || '',
      });
      onChanged && onChanged();
      return d?.ok ? `Written down as a ${d.kind} on this board.` : 'Could not write that down.';
    }
    if (name === 'send_to_greg') {
      const d = await neonQuery(q('handoff'), { title: input?.title, detail: input?.detail || '' });
      onChanged && onChanged();
      return d?.ok
        ? `Added to Greg's list as item ${d.backlogId}. It will show in his brief.`
        : 'That did not save. Tell them to raise it with Greg directly.';
    }
    return `There is no tool called ${name}.`;
  };

  const send = async (textOverride) => {
    const text = (textOverride ?? draft).trim();
    const sending = textOverride ? [] : attachments;
    if ((!text && !sending.length) || thinking || uploading) return;
    setDraft(''); setAttachments([]); attachRef.current = []; setErr('');

    const pics = sending.filter(a => a.kind === 'image');
    const docs = sending.filter(a => a.kind !== 'image');
    // A file we cannot read is still named in the message, so the answer is "save
    // that as a PDF and I will read it" rather than a reply that quietly pretends
    // nothing was attached.
    const unreadable = docs.filter(d => !d.readable).map(d => d.name);
    const mine = {
      role: 'user',
      text: [text, unreadable.length ? `(also attached, but not readable: ${unreadable.join(', ')})` : '']
        .filter(Boolean).join('\n'),
      images: pics.map(p => ({ name: p.name, base64: p.base64, mediaType: p.mediaType, key: p.r2Key || null })),
      docs,
    };
    const shown = [...messages, mine];
    setMessages(shown);
    setThinking(true);

    // Keep the picture as well as ask about it. A screenshot that answers a question is
    // evidence, and evidence belongs on the task, not only in a chat window. Silent, so
    // it does not write a note per screenshot and bury the real ones. Everything that
    // is not a screenshot was already stored on the way in.
    const fresh = pics.filter(p => p.file && !p.r2Key);
    if (fresh.length) {
      Promise.all(fresh.map(p => uploadFile(
        new File([p.file], p.name, { type: p.file.type || 'image/png' }), { silent: true }
      ))).catch(() => {});
    }

    try {
      // The tool exchange runs against a LOCAL copy of the conversation and never
      // enters the stored transcript. The transcript keeps its shape (a person's
      // words, then an answer), and what the tools actually did is in the answer,
      // which is stored. Restructuring how this panel stores a conversation to hold
      // tool blocks would risk every existing thread for no gain to the reader.
      let apiMsgs = await toApi(shown.filter(m => m.role !== 'system'));
      let reply = '';

      for (let round = 0; round < MAX_TOOL_ROUNDS; round++) {
        const data = await callClaude(apiMsgs);
        reply = (data.content || []).filter(c => c.type === 'text').map(c => c.text).join('\n').trim();

        const calls = (data.content || []).filter(b => b.type === 'tool_use');
        if (data.stop_reason !== 'tool_use' || !calls.length) break;

        apiMsgs = [...apiMsgs, { role: 'assistant', content: data.content || [] }];
        const results = [];
        for (const c of calls) {
          let out;
          try { out = await runTool(c.name, c.input || {}); }
          catch (e) { out = `That did not work: ${e.message}`; }
          results.push({ type: 'tool_result', tool_use_id: c.id, content: String(out).slice(0, 12000) });
        }
        apiMsgs = [...apiMsgs, { role: 'user', content: results }];

        // Out of rounds with a tool result still unanswered: ask once more without
        // looping, so a turn never ends on a dangling tool call.
        if (round === MAX_TOOL_ROUNDS - 1) {
          const final = await callClaude(apiMsgs);
          reply = (final.content || []).filter(c => c.type === 'text').map(c => c.text).join('\n').trim();
          // Tool calls in that last answer are usually the write the turn was building
          // toward. Run them rather than drop them silently.
          for (const c of (final.content || []).filter(b => b.type === 'tool_use')) {
            try { await runTool(c.name, c.input || {}); } catch (_) { /* the reply still stands */ }
          }
        }
      }

      const next = [...shown, { role: 'assistant', text: reply || '(no answer came back)' }];
      setMessages(next);
      persist(next);
    } catch (e) {
      const said = explainSendFailure(e.message, sending.length > 0);
      setErr(said);
      const next = [...shown, { role: 'assistant', text: `I could not answer just then: ${said}` }];
      setMessages(next);
      persist(next);
    } finally { setThinking(false); }
  };

  const markDone = async () => {
    await neonQuery(q('item_upsert'), { id: item.id, status: 'done' });
    onChanged && onChanged();
    onClose();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,20,40,.5)', zIndex: 90, display: 'flex', justifyContent: 'flex-end' }}
      onClick={onClose}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}>
      <div onClick={e => e.stopPropagation()}
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onPaste={e => {
          if (!dragCarriesFiles(e.clipboardData)) return;
          e.preventDefault();
          filesFromDrop(e.clipboardData).then(takeFiles);
        }}
        style={{ position: 'relative', width: 'min(760px,100%)', background: '#fff', height: '100%', display: 'flex', flexDirection: 'column' }}>

        {dragging && (
          <div style={{
            position: 'absolute', inset: 0, zIndex: 5, pointerEvents: 'none',
            background: 'rgba(255,246,234,.94)', border: '3px dashed var(--orange)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10,
          }}>
            <Upload size={40} color="var(--orange)" />
            <div style={{ fontSize: 19, fontWeight: 700, color: 'var(--navy)' }}>Drop it anywhere here</div>
            <div style={{ fontSize: 14, color: '#3d4b58' }}>
              PDFs, spreadsheets, statements, screenshots. It attaches to the task and I read it. Up to {prettySize(MAX_ATTACH_BYTES)}.
            </div>
          </div>
        )}

        <div style={{ background: 'var(--navy)', color: '#fff', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <div style={{ fontSize: 11, letterSpacing: '.14em', textTransform: 'uppercase', color: '#9dc4dd' }}>Working on</div>
            <div style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.3 }}>{item.title}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer' }}><X size={20} /></button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 20px' }}>
          {item.how_to ? (
            <div className="markdown-body" style={{ lineHeight: 1.65, fontSize: 15 }}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{item.how_to}</ReactMarkdown>
            </div>
          ) : (
            <div style={{ color: '#5a6b7a' }}>{item.detail || 'No written instructions on this one yet. Ask below and we will work it out together.'}</div>
          )}

          <div style={{
            marginTop: 20, borderRadius: 6, padding: 16, textAlign: 'center',
            border: dragging ? '2px dashed var(--orange)' : '2px dashed #c3d0da',
            background: dragging ? '#fff6ea' : '#f8fafc',
          }}>
            <Upload size={20} color={dragging ? 'var(--orange)' : '#8b94a3'} />
            <div style={{ marginTop: 6, color: '#3d4b58', fontSize: 14.5 }}>
              {item.wants_file
                ? 'Drop the exported file here'
                : 'Attach anything useful: a PDF, an export, a marked-up spreadsheet, a screenshot'}
            </div>
            <div style={{ marginTop: 4, color: '#8b94a3', fontSize: 12.5 }}>
              Drag it anywhere on this panel, paste a screenshot with Ctrl+V, or
            </div>
            <button className="btn btn-outline" style={{ marginTop: 8 }} disabled={uploading}
              onClick={() => fileRef.current?.click()}>
              {uploading ? 'Uploading…' : 'choose a file'}
            </button>
            <input ref={fileRef} type="file" multiple style={{ display: 'none' }}
              onChange={e => { takeFiles(e.target.files); e.target.value = ''; }} />
          </div>

          {myFiles.length > 0 && (
            <div style={{ marginTop: 14 }}>
              {myFiles.map(f => (
                <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', fontSize: 14 }}>
                  <FileText size={15} color="#1c6b3f" />
                  <button onClick={() => viewFile(f)}
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--navy)', fontWeight: 600, textDecoration: 'underline', fontSize: 14 }}>
                    {f.filename}
                  </button>
                  <span style={{ color: '#8b94a3', fontSize: 12 }}>
                    {Math.round((f.byte_size || 0) / 1024)} KB, {f.uploaded_by_name}
                  </span>
                </div>
              ))}
            </div>
          )}

          {messages.length > 0 && <div style={{ borderTop: '1px solid #dbe3ea', margin: '20px 0 14px' }} />}
          {restored && messages.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 12, fontSize: 12.5, color: '#5a6b7a' }}>
              <span>Picking up where you left off.</span>
              <button className="btn btn-ghost" style={{ fontSize: 12, padding: '3px 9px', border: '1px solid #dbe3ea' }}
                onClick={clearChat}>
                <RotateCcw size={12} style={{ marginRight: 5, verticalAlign: -1 }} />Start a fresh conversation
              </button>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} style={{ marginBottom: 12, display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
              <div style={{
                maxWidth: '82%', padding: '10px 14px', borderRadius: 8, fontSize: 14.5, lineHeight: 1.6,
                background: m.role === 'user' ? 'var(--navy)' : m.role === 'system' ? '#e6f5ec' : '#f2f5f8',
                color: m.role === 'user' ? '#fff' : '#1f2d3a',
              }}>
                {(m.images || []).length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: m.text ? 8 : 0 }}>
                    {m.images.map((p, k) => (p.base64 ? (
                      <img key={k} src={`data:${p.mediaType || 'image/jpeg'};base64,${p.base64}`} alt={p.name}
                        style={{ maxWidth: 220, maxHeight: 160, borderRadius: 5, display: 'block' }} />
                    ) : (
                      <span key={k} style={{ fontSize: 12.5, opacity: .85, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        <ImageIcon size={13} /> {p.name}
                      </span>
                    )))}
                  </div>
                )}
                {(m.docs || []).length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: m.text ? 8 : 0 }}>
                    {m.docs.map((d, k) => (
                      <span key={k} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, padding: '2px 7px', borderRadius: 20,
                        background: m.role === 'user' ? 'rgba(255,255,255,.16)' : '#eef3f8',
                      }}><FileText size={12} /> {d.name}</span>
                    ))}
                  </div>
                )}
                {m.role === 'assistant'
                  ? <div className="markdown-body"><ReactMarkdown remarkPlugins={[remarkGfm]}>{m.text}</ReactMarkdown></div>
                  : m.text}
              </div>
            </div>
          ))}
          {thinking && <div style={{ color: '#8b94a3', fontSize: 14 }}><Loader size={14} className="spin" /> thinking…</div>}
          <div ref={endRef} />
        </div>

        {err && <div style={{ padding: '8px 20px', background: '#fdecec', color: '#a12020', fontSize: 13 }}>{err}</div>}

        <div style={{ borderTop: '1px solid #dbe3ea', padding: '12px 20px 16px' }}>
          {!loadingChat && messages.length === 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
              {OPENERS.map(o => (
                <button key={o} className="btn btn-ghost" style={{ fontSize: 12, padding: '4px 10px', border: '1px solid #dbe3ea' }}
                  onClick={() => send(o)}>{o}</button>
              ))}
            </div>
          )}

          {attachments.length > 0 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
              {attachments.map(a => (
                <div key={a.id} style={{ position: 'relative' }}>
                  {a.kind === 'image' ? (
                    <img src={`data:${a.mediaType};base64,${a.base64}`} alt={a.name}
                      style={{ width: 74, height: 56, objectFit: 'cover', borderRadius: 5, border: '1px solid #dbe3ea', display: 'block' }} />
                  ) : (
                    <div title={a.readable ? 'Saved on the task, and I will read it' : 'Saved on the task, but I cannot read inside this one'}
                      style={{
                        width: 132, height: 56, borderRadius: 5, display: 'flex', flexDirection: 'column', justifyContent: 'center',
                        padding: '0 8px', border: '1px solid #dbe3ea', background: a.readable ? '#f2f5f8' : '#fdf3e6',
                      }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#1f2d3a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <FileText size={13} /> {a.name}
                      </div>
                      <div style={{ fontSize: 11, color: a.readable ? '#6b7a89' : '#a35a12' }}>
                        {a.readable ? `${a.kind === 'pdf' ? 'PDF' : 'read as text'}${a.bytes ? `, ${prettySize(a.bytes)}` : ''}` : 'not readable'}
                      </div>
                    </div>
                  )}
                  <button onClick={() => removeAttachment(a.id)} title="Take this off"
                    style={{
                      position: 'absolute', top: -6, right: -6, width: 19, height: 19, borderRadius: '50%',
                      border: 'none', background: 'var(--navy)', color: '#fff', cursor: 'pointer',
                      lineHeight: '19px', padding: 0, fontSize: 11,
                    }}>&times;</button>
                </div>
              ))}
              <span style={{ alignSelf: 'center', fontSize: 12, color: '#8b94a3' }}>
                Goes with your next message.
              </span>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <button className="btn btn-ghost" onClick={toggleMic} title={listening ? 'Stop dictating' : 'Dictate'}
              style={{ color: listening ? 'var(--orange)' : '#5a6b7a', border: '1px solid #dbe3ea' }}>
              {listening ? <MicOff size={17} /> : <Mic size={17} />}
            </button>
            <textarea className="form-textarea" rows={2} value={draft}
              placeholder={listening ? 'Listening…' : 'Ask anything about this task, or paste a screenshot with Ctrl+V'}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              style={{ flex: 1, resize: 'none' }} />
            <button className="btn btn-primary" onClick={() => send()} disabled={thinking || (!draft.trim() && !attachments.length)}><Send size={16} /></button>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, gap: 10 }}>
            <span style={{ fontSize: 12, color: '#8b94a3' }}>
              {listening
                ? 'Speak, then press the microphone again to stop.'
                : 'Enter sends. Shift+Enter for a new line. This conversation is kept.'}
            </span>
            <button className="btn btn-outline" onClick={markDone}>
              <CheckCircle2 size={15} style={{ marginRight: 6 }} /> Mark this done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
